import { useMemo, useRef, useState } from "react";
import {
  Box,
  InputAdornment,
  Stack,
  TablePagination,
  TextField,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import { EvolutionConfirmDialog } from "@/components/schedule/evolution/EvolutionConfirmDialog";
import { toEvolutionPatient } from "@/components/schedule/evolution/evolutionPatient";
import { usePatientEvolution } from "@/components/schedule/evolution/usePatientEvolution";
import { useSessionContext } from "@/contexts/SessionContext";
import {
  mapFhirPatientToMessagingPatient,
  useConsultationWhatsAppSend,
} from "@/pages/adminInsights/utils/mapConsultationToWhatsApp";
import { useFitRowCount } from "../shell/useFitRowCount";
import { InsightPatientsBatchBar } from "./InsightPatientsBatchBar";
import { InsightPatientsPanelHeader } from "./InsightPatientsPanelHeader";
import { InsightPatientsTable } from "./InsightPatientsTable";
import type { InsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import type { InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

const TABLE_ROW_HEIGHT = 52;

interface InsightPatientsPanelProps {
  accentColor: string;
  rows: InsightPatientRow[];
  totalPatientCount: number;
  hasActiveFilter: boolean;
  statusLabelByKey: Record<string, string>;
  texts: InsightAnalysisTexts;
  fillHeight?: boolean;
  /** Default: botão Evoluir visível. */
  showEvolve?: boolean;
}

export function InsightPatientsPanel({
  accentColor,
  rows,
  totalPatientCount,
  hasActiveFilter,
  statusLabelByKey,
  texts,
  fillHeight = false,
  showEvolve = true,
}: InsightPatientsPanelProps) {
  const { access } = useSessionContext();
  const isProfessional = access?.type === "professional";
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [evolutionOpen, setEvolutionOpen] = useState(false);
  const [evolutionPatient, setEvolutionPatient] = useState<InsightPatientRow | null>(null);
  const tableAreaRef = useRef<HTMLDivElement>(null);
  const rowsPerPage = useFitRowCount(tableAreaRef, TABLE_ROW_HEIGHT, 4);

  const { openSendBatch, isTemplatesLoading, hasTemplates, sendDialogProps } =
    useConsultationWhatsAppSend();
  const {
    loading: evolutionLoading,
    hasOpenEvolution,
    peekOpenEvolution,
    startOrContinueEvolution,
    startOrContinueFromPatientId,
    professionalDisplay,
  } = usePatientEvolution();

  const whatsAppDisabled = isTemplatesLoading || !hasTemplates;
  const whatsAppTooltip = isTemplatesLoading
    ? "Carregando modelos..."
    : hasTemplates
      ? "Enviar WhatsApp"
      : "Nenhum modelo de mensagem disponível";

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => row.patientName.toLowerCase().includes(q));
  }, [rows, search]);

  const pageRows = useMemo(
    () =>
      filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const pageIds = pageRows.map((row) => row.patientId);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  const selectedPatients = useMemo(
    () =>
      rows
        .filter((row) => selectedIds.has(row.patientId))
        .map((row) => mapFhirPatientToMessagingPatient(row.patient))
        .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry)),
    [rows, selectedIds]
  );

  const from = filteredRows.length === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(filteredRows.length, (page + 1) * rowsPerPage);

  return (
    <Stack
      spacing={2}
      sx={{
        width: "100%",
        flex: fillHeight ? 1 : undefined,
        minHeight: 0,
        height: fillHeight ? "100%" : undefined,
      }}
    >
      <InsightPatientsPanelHeader
        filteredCount={rows.length}
        totalCount={totalPatientCount}
        hasActiveFilter={hasActiveFilter}
        accentColor={accentColor}
      />

      <TextField
        size="small"
        placeholder="Buscar paciente"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box
        ref={tableAreaRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <InsightPatientsTable
          pageRows={pageRows}
          statusLabelByKey={statusLabelByKey}
          texts={texts}
          selectedIds={selectedIds}
          expandedId={expandedId}
          accentColor={accentColor}
          isProfessional={isProfessional}
          whatsAppDisabled={whatsAppDisabled}
          whatsAppTooltip={whatsAppTooltip}
          allPageSelected={allPageSelected}
          somePageSelected={somePageSelected}
          onToggleSelectAllPage={() =>
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (allPageSelected) pageIds.forEach((id) => next.delete(id));
              else pageIds.forEach((id) => next.add(id));
              return next;
            })
          }
          onToggleSelect={(patientId) =>
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(patientId)) next.delete(patientId);
              else next.add(patientId);
              return next;
            })
          }
          onEvolve={async (row) => {
            await peekOpenEvolution(row.patient?.id ?? row.patientId);
            setEvolutionPatient(row);
            setEvolutionOpen(true);
          }}
          onWhatsApp={(row) => {
            const patient = mapFhirPatientToMessagingPatient(row.patient);
            if (patient) openSendBatch({ patients: [patient] });
          }}
          onToggleExpand={(patientId) =>
            setExpandedId((current) => (current === patientId ? null : patientId))
          }
          showEvolve={showEvolve}
        />
      </Box>

      <InsightPatientsBatchBar
        selectedCount={selectedIds.size}
        whatsAppTooltip={whatsAppTooltip}
        whatsAppDisabled={whatsAppDisabled}
        canSendBatch={selectedPatients.length > 0}
        onSendBatch={() => openSendBatch({ patients: selectedPatients })}
        onClearSelection={() => setSelectedIds(new Set())}
      />

      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={(_e, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
        labelRowsPerPage=""
        labelDisplayedRows={() => `${from}–${to} de ${filteredRows.length}`}
        sx={{
          flexShrink: 0,
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-select": {
            display: "none",
          },
        }}
      />

      <SendWhatsAppMessageDialog {...sendDialogProps} />
      <EvolutionConfirmDialog
        open={evolutionOpen}
        patientDisplay={evolutionPatient?.patientName}
        professionalDisplay={professionalDisplay}
        hasOpenEvolution={hasOpenEvolution}
        loading={evolutionLoading}
        onClose={() => {
          setEvolutionOpen(false);
          setEvolutionPatient(null);
        }}
        onConfirm={async () => {
          if (!evolutionPatient) return;
          const ok = evolutionPatient.patient?.id
            ? await startOrContinueEvolution(
                toEvolutionPatient(evolutionPatient.patient)
              )
            : await startOrContinueFromPatientId(evolutionPatient.patientId);
          if (ok) {
            setEvolutionOpen(false);
            setEvolutionPatient(null);
          }
        }}
      />
    </Stack>
  );
}
